import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import {
  validateAvalCurp,
  VALIDATE_AVAL_CURP
} from 'src/store/actions/avalBuroFormActions';
import theme from 'src/theme';
import useStyles from '../styles';
import makeInitialState from './initialState';
import validationSchema from './schema';

const StepOne = ({ data, onNext, onBack }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const initialState = useMemo(() => makeInitialState(data), [data]);
  const isValidating = useSelector(isLoadingSelector([VALIDATE_AVAL_CURP.action]));

  const handleSubmit = useCallback(
    values => {
      dispatch(validateAvalCurp(values, onNext));
    },
    [dispatch, onNext]
  );

  return (
    <Formik
      initialValues={initialState}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        errors,
        handleSubmit: formikSubmit,
        touched,
        values,
        handleBlur,
        handleChange
      }) => (
        <form onSubmit={formikSubmit}>
          <Box>
            <Typography
              variant="h3"
              sx={{ marginBottom: '8px', color: theme.palette.primary.main }}
            >
              {t('AVAL_BURO_FORM.STEP_ONE.TITLE')}
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: '20px' }}>
              {t('AVAL_BURO_FORM.STEP_ONE.DESCRIPTION')}
            </Typography>

            <TextField
              error={Boolean(touched.rfc && errors.rfc)}
              fullWidth
              helperText={touched.rfc && errors.rfc ? t(errors.rfc) : undefined}
              label="RFC"
              margin="normal"
              name="rfc"
              onBlur={handleBlur}
              onChange={handleChange}
              onInput={e => {
                e.target.value = e.target.value.toUpperCase();
              }}
              value={values.rfc}
              variant="outlined"
              inputProps={{ maxLength: 13, autoCapitalize: 'characters' }}
            />

            <TextField
              error={Boolean(touched.curp && errors.curp)}
              fullWidth
              helperText={touched.curp && errors.curp ? t(errors.curp) : undefined}
              label="CURP"
              margin="normal"
              name="curp"
              onBlur={handleBlur}
              onChange={handleChange}
              onInput={e => {
                e.target.value = e.target.value.toUpperCase();
              }}
              value={values.curp}
              variant="outlined"
              inputProps={{ maxLength: 18, autoCapitalize: 'characters' }}
            />
          </Box>

          <Box className={classes.box}>
            <Button
              className={classes.buttons}
              onClick={onBack}
              color="primary"
              variant="outlined"
            >
              {t('COMMON.BACK')}
            </Button>
            <Button
              className={clsx(classes.buttons, classes.buttonMain)}
              type="submit"
              disabled={isValidating}
              color="primary"
              variant="contained"
              startIcon={isValidating ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {t('COMMON.NEXT')}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

StepOne.propTypes = {
  data: PropTypes.object,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export default StepOne;
